import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { US_STATES } from "@/lib/usStates";
import { Building2, Briefcase, MapPin, Users, Layers, Plus, Pencil, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import CompanyAdminsCard from "./CompanyAdminsCard";

type OwnerType = "agency" | "client" | "prospect";

interface OrganizationManagerProps {
  ownerType: OwnerType;
  ownerId: string | number;
  readOnly?: boolean;
}

interface OrgItem {
  id: number;
  name?: string;
  officeName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  county?: string;
}

type CategoryType = "department" | "title" | "location" | "class" | "division";

const categoryConfig: Record<CategoryType, {
  icon: React.ElementType;
  title: string;
  description: string;
  endpoint: string;
  color: string;
}> = {
  department: {
    icon: Building2,
    title: "Departments",
    description: "Organizational units within the organization",
    endpoint: "/api/org/departments",
    color: "text-blue-600 bg-blue-50",
  },
  title: {
    icon: Briefcase,
    title: "Job Titles",
    description: "Positions and roles for employees",
    endpoint: "/api/org/job-titles",
    color: "text-purple-600 bg-purple-50",
  },
  location: {
    icon: MapPin,
    title: "Locations",
    description: "Office locations and work sites",
    endpoint: "/api/org/locations",
    color: "text-green-600 bg-green-50",
  },
  class: {
    icon: Users,
    title: "Employee Classes",
    description: "Classification groups for benefits eligibility",
    endpoint: "/api/org/employee-classes",
    color: "text-orange-600 bg-orange-50",
  },
  division: {
    icon: Layers,
    title: "Divisions",
    description: "Business divisions or units",
    endpoint: "/api/org/divisions",
    color: "text-teal-600 bg-teal-50",
  },
};

export default function OrganizationManager({ ownerType, ownerId, readOnly }: OrganizationManagerProps) {
  const qc = useQueryClient();
  const { toast } = useToast();

  // Same component serves the brokerage's own org and employer/prospect records.
  const ownerLabel = ownerType === "agency" ? "your agency" : "this company";

  const ownerQueryKey = (base: string) => [base, ownerType, ownerId];
  const ownerPayload =
    ownerType === "client"
      ? { ownerType: "client", clientId: ownerId }
      : ownerType === "prospect"
      ? { ownerType: "prospect", prospectId: ownerId }
      : { ownerType: "agency", agencyId: String(ownerId) };

  // Fetch all data
  const { data: departments = [] } = useQuery({
    queryKey: ownerQueryKey("/api/org/departments"),
    queryFn: async () => {
      const r = await fetch(`/api/org/departments?ownerType=${ownerType}&ownerId=${ownerId}`);
      return r.ok ? r.json() : [];
    },
  });

  const { data: jobTitles = [] } = useQuery({
    queryKey: ownerQueryKey("/api/org/job-titles"),
    queryFn: async () => {
      const r = await fetch(`/api/org/job-titles?ownerType=${ownerType}&ownerId=${ownerId}`);
      return r.ok ? r.json() : [];
    },
  });

  const { data: locations = [] } = useQuery({
    queryKey: ownerQueryKey("/api/org/locations"),
    queryFn: async () => {
      const r = await fetch(`/api/org/locations?ownerType=${ownerType}&ownerId=${ownerId}`);
      return r.ok ? r.json() : [];
    },
  });

  const { data: classes = [] } = useQuery({
    queryKey: ownerQueryKey("/api/org/employee-classes"),
    queryFn: async () => {
      const r = await fetch(`/api/org/employee-classes?ownerType=${ownerType}&ownerId=${ownerId}`);
      return r.ok ? r.json() : [];
    },
  });

  const { data: divisions = [] } = useQuery({
    queryKey: ownerQueryKey("/api/org/divisions"),
    queryFn: async () => {
      const r = await fetch(`/api/org/divisions?ownerType=${ownerType}&ownerId=${ownerId}`);
      return r.ok ? r.json() : [];
    },
  });

  // Dialog state
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState<"add" | "edit">("add");
  const [currentCategory, setCurrentCategory] = React.useState<CategoryType>("department");
  const [editingItem, setEditingItem] = React.useState<OrgItem | null>(null);

  // Form state
  const [formName, setFormName] = React.useState("");
  const [formOffice, setFormOffice] = React.useState("");
  const [formAddress1, setFormAddress1] = React.useState("");
  const [formAddress2, setFormAddress2] = React.useState("");
  const [formCity, setFormCity] = React.useState("");
  const [formState, setFormState] = React.useState("");
  const [formZipCode, setFormZipCode] = React.useState("");
  const [formCounty, setFormCounty] = React.useState("");

  const resetForm = () => {
    setFormName("");
    setFormOffice("");
    setFormAddress1("");
    setFormAddress2("");
    setFormCity("");
    setFormState("");
    setFormZipCode("");
    setFormCounty("");
    setEditingItem(null);
  };

  const openAddDialog = (category: CategoryType) => {
    resetForm();
    setCurrentCategory(category);
    setDialogMode("add");
    setDialogOpen(true);
  };

  const openEditDialog = (category: CategoryType, item: OrgItem) => {
    setCurrentCategory(category);
    setDialogMode("edit");
    setEditingItem(item);
    if (category === "location") {
      setFormOffice(item.officeName || "");
      setFormAddress1(item.address1 || "");
      setFormAddress2(item.address2 || "");
      setFormCity(item.city || "");
      setFormState(item.state || "");
      setFormZipCode(item.zipCode || "");
      setFormCounty(item.county || "");
    } else {
      setFormName(item.name || "");
    }
    setDialogOpen(true);
  };

  // Mutations
  const createMutation = useMutation({
    mutationFn: async ({ category, data }: { category: CategoryType; data: any }) => {
      const endpoint = categoryConfig[category].endpoint;
      const r = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...ownerPayload, ...data }),
      });
      if (!r.ok) throw new Error("Failed to create");
      return r.json();
    },
    onSuccess: (_, { category }) => {
      qc.invalidateQueries({ queryKey: ownerQueryKey(categoryConfig[category].endpoint) });
      toast({ title: "Created successfully", variant: "success" });
      setDialogOpen(false);
      resetForm();
    },
    onError: () => {
      toast({ title: "Failed to create", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ category, id, data }: { category: CategoryType; id: number; data: any }) => {
      const endpoint = categoryConfig[category].endpoint;
      const r = await fetch(`${endpoint}/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error("Failed to update");
      return r.json();
    },
    onSuccess: (_, { category }) => {
      qc.invalidateQueries({ queryKey: ownerQueryKey(categoryConfig[category].endpoint) });
      toast({ title: "Updated successfully", variant: "success" });
      setDialogOpen(false);
      resetForm();
    },
    onError: () => {
      toast({ title: "Failed to update", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ category, id }: { category: CategoryType; id: number }) => {
      const endpoint = categoryConfig[category].endpoint;
      const r = await fetch(`${endpoint}/${id}`, { method: "DELETE" });
      if (!r.ok) throw new Error("Failed to delete");
    },
    onSuccess: (_, { category }) => {
      qc.invalidateQueries({ queryKey: ownerQueryKey(categoryConfig[category].endpoint) });
      toast({ title: "Deleted successfully", variant: "success" });
    },
    onError: () => {
      toast({ title: "Failed to delete", variant: "destructive" });
    },
  });

  const handleSubmit = () => {
    if (currentCategory === "location") {
      const data = {
        officeName: formOffice,
        address1: formAddress1 || undefined,
        address2: formAddress2 || undefined,
        city: formCity,
        state: formState,
        zipCode: formZipCode || undefined,
        county: formCounty || undefined,
      };
      if (!formOffice.trim()) return;
      if (dialogMode === "add") {
        createMutation.mutate({ category: currentCategory, data });
      } else if (editingItem) {
        updateMutation.mutate({ category: currentCategory, id: editingItem.id, data });
      }
    } else {
      if (!formName.trim()) return;
      const data = { name: formName.trim() };
      if (dialogMode === "add") {
        createMutation.mutate({ category: currentCategory, data });
      } else if (editingItem) {
        updateMutation.mutate({ category: currentCategory, id: editingItem.id, data });
      }
    }
  };

  const getItemDisplay = (category: CategoryType, item: OrgItem) => {
    if (category === "location") {
      const addr = [item.address1, item.city, item.state].filter(Boolean).join(", ");
      const zip = item.zipCode ? ` ${item.zipCode}` : "";
      return item.officeName + (addr ? ` • ${addr}${zip}` : "");
    }
    return item.name || "";
  };

  const getCategoryData = (category: CategoryType): OrgItem[] => {
    switch (category) {
      case "department": return departments;
      case "title": return jobTitles;
      case "location": return locations;
      case "class": return classes;
      case "division": return divisions;
    }
  };

  const renderCategoryCard = (category: CategoryType) => {
    const config = categoryConfig[category];
    const Icon = config.icon;
    const items = getCategoryData(category);
    const [iconColor, iconBg] = config.color.split(" ");

    return (
      <Card key={category} className="flex flex-col">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn("p-2 rounded-lg", iconBg)}>
                <Icon className={cn("h-5 w-5", iconColor)} />
              </div>
              <div>
                <CardTitle className="text-base">{config.title}</CardTitle>
                  <CardDescription className="text-xs">
                  {category === "department" ? `Organizational units within ${ownerLabel}` : config.description}
                </CardDescription>
              </div>
            </div>
            {!readOnly && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => openAddDialog(category)}
                className="h-8"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="flex-1 pt-0">
          {items.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <Icon className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No {config.title.toLowerCase()} yet</p>
              {!readOnly && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => openAddDialog(category)}
                  className="mt-1"
                >
                  Add your first one
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-2.5 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors group"
                >
                  <span className="text-sm font-medium truncate flex-1">
                    {getItemDisplay(category, item)}
                  </span>
                  {!readOnly && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => openEditDialog(category, item)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => deleteMutation.mutate({ category, id: item.id })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const isFormValid = currentCategory === "location"
    ? formOffice.trim() && formCity.trim() && formState.trim()
    : formName.trim();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Organization Structure</h2>
        <p className="text-muted-foreground">
          Define departments, job titles, locations, and employee classifications for {ownerLabel}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <CompanyAdminsCard ownerType={ownerType} ownerId={ownerId} readOnly={readOnly} />
        {(Object.keys(categoryConfig) as CategoryType[]).map(renderCategoryCard)}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === "add" ? "Add" : "Edit"} {categoryConfig[currentCategory].title.slice(0, -1)}
            </DialogTitle>
            <DialogDescription>
              {dialogMode === "add"
                ? `Create a new ${categoryConfig[currentCategory].title.toLowerCase().slice(0, -1)}`
                : `Update the ${categoryConfig[currentCategory].title.toLowerCase().slice(0, -1)} details`}
            </DialogDescription>
          </DialogHeader>

          {currentCategory === "location" ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="office">Office Name</Label>
                <Input
                  id="office"
                  placeholder="e.g., Headquarters"
                  value={formOffice}
                  onChange={(e) => setFormOffice(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address1">Address</Label>
                <Input
                  id="address1"
                  placeholder="Street address"
                  value={formAddress1}
                  onChange={(e) => setFormAddress1(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address2">Address Line 2</Label>
                <Input
                  id="address2"
                  placeholder="Suite, unit, floor (optional)"
                  value={formAddress2}
                  onChange={(e) => setFormAddress2(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    placeholder="e.g., New York"
                    value={formCity}
                    onChange={(e) => setFormCity(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Select value={formState || undefined} onValueChange={setFormState}>
                    <SelectTrigger id="state">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((s) => (
                        <SelectItem key={s.code} value={s.code}>
                          {s.name} ({s.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="zipCode">ZIP Code</Label>
                  <Input
                    id="zipCode"
                    placeholder="e.g., 10001"
                    value={formZipCode}
                    onChange={(e) => setFormZipCode(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="county">County</Label>
                  <Input
                    id="county"
                    placeholder="e.g., Manhattan"
                    value={formCounty}
                    onChange={(e) => setFormCounty(e.target.value)}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder={`Enter ${categoryConfig[currentCategory].title.toLowerCase().slice(0, -1)} name`}
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                autoFocus
              />
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isFormValid || createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? "Saving..."
                : dialogMode === "add"
                ? "Create"
                : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
